<template>
    <div class="space-y-6">
        <div>
            <h1 class="text-2xl font-semibold text-gray-900">
                Manage Asset Locations
            </h1>

            <p class="mt-1 text-sm text-gray-500">
                Manage asset locations associated with each experience.
            </p>
        </div>

        <div
            v-if="error"
            class="rounded-md bg-red-50 p-4 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div class="rounded-lg border border-gray-200 bg-white p-4">
            <label class="mb-1 block text-sm font-medium text-gray-700">
                Search Experiences
            </label>

            <div class="flex gap-2">
                <input
                    v-model="search"
                    type="text"
                    class="w-full rounded-md border border-gray-300 px-3 py-2"
                    placeholder="Search by name, friendly name or Place ID..."
                    @keyup.enter="searchExperiences"
                >

                <button
                    type="button"
                    class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    @click="searchExperiences"
                >
                    Search
                </button>

                <button
                    v-if="search"
                    type="button"
                    class="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                    @click="clearSearch"
                >
                    Clear
                </button>
            </div>
        </div>

        <div
            v-if="loading"
            class="py-10 text-center text-sm text-gray-500"
        >
            Loading experiences...
        </div>

        <div
            v-else-if="!experiences.length"
            class="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500"
        >
            No experiences found.
        </div>

        <div
            v-else
            class="space-y-3"
        >
            <div
                v-for="experience in experiences"
                :key="experience.uuid"
                class="overflow-hidden rounded-lg border border-gray-200 bg-white"
            >
                <button
                    type="button"
                    class="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-gray-50"
                    @click="toggleExperience(experience)"
                >
                    <div class="flex min-w-0 items-center gap-4">
                        <img
                            v-if="experience.img_url"
                            :src="experience.img_url"
                            class="h-12 w-12 shrink-0 rounded-md object-cover"
                        >

                        <div class="min-w-0">
                            <div class="font-medium text-gray-900">
                                {{ experience.friendly_name || experience.name }}
                            </div>

                            <div
                                v-if="experience.friendly_name && experience.name"
                                class="truncate text-xs text-gray-500"
                            >
                                {{ experience.name }}
                            </div>

                            <div class="mt-1 text-xs text-gray-400">
                                Place ID: {{ experience.place_id ?? '-' }}
                            </div>
                        </div>
                    </div>

                    <div class="flex shrink-0 items-center gap-4">
                        <span class="text-sm text-gray-500">
                            {{ getAssetLocationCount(experience.uuid) }}
                            locations
                        </span>

                        <span class="text-lg text-gray-400">
                            {{
                                expandedExperienceUuid === experience.uuid
                                    ? '−'
                                    : '+'
                            }}
                        </span>
                    </div>
                </button>

                <div
                    v-if="expandedExperienceUuid === experience.uuid"
                    class="border-t border-gray-200 bg-gray-50 p-4"
                >
                    <div
                        v-if="isLoadingAssetLocations(experience.uuid)"
                        class="py-6 text-center text-sm text-gray-500"
                    >
                        Loading asset locations...
                    </div>

                    <template v-else>
                        <div class="mb-5 rounded-md border border-gray-200 bg-white p-4">
                            <h3 class="mb-4 font-medium text-gray-900">
                                Add Asset Location
                            </h3>

                            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label class="mb-1 block text-sm font-medium text-gray-700">
                                        Name
                                    </label>

                                    <input
                                        v-model="forms[experience.uuid].name"
                                        type="text"
                                        class="w-full rounded-md border border-gray-300 px-3 py-2"
                                        placeholder="e.g. Main Billboard"
                                    >
                                </div>

                                <div>
                                    <label class="mb-1 block text-sm font-medium text-gray-700">
                                        Asset Location UUID
                                    </label>

                                    <input
                                        v-model="forms[experience.uuid].uuid"
                                        type="text"
                                        class="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    >

                                    <p class="mt-1 text-xs text-gray-500">
                                        Enter the UUID used by the location inside the game.
                                    </p>
                                </div>

                                <div class="md:col-span-2">
                                    <label class="mb-1 block text-sm font-medium text-gray-700">
                                        Image
                                    </label>

                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        class="block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                                        @change="selectImage(experience.uuid, $event)"
                                    >

                                    <p class="mt-1 text-xs text-gray-500">
                                        Optional. JPG, PNG or WebP. Maximum 5 MB.
                                    </p>
                                </div>
                            </div>

                            <div
                                v-if="getFormError(experience.uuid)"
                                class="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700"
                            >
                                {{ getFormError(experience.uuid) }}
                            </div>

                            <div class="mt-4 flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="isSaving(experience.uuid)"
                                    @click="createAssetLocation(experience)"
                                >
                                    {{
                                        isSaving(experience.uuid)
                                            ? 'Adding...'
                                            : 'Add Asset Location'
                                    }}
                                </button>
                            </div>
                        </div>

                        <div
                            v-if="!getAssetLocations(experience.uuid).length"
                            class="rounded-md border border-gray-200 bg-white p-6 text-center text-sm text-gray-500"
                        >
                            No asset locations have been created for this experience.
                        </div>

                        <div
                            v-else
                            class="overflow-hidden rounded-md border border-gray-200 bg-white"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                            Name
                                        </th>

                                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                            UUID
                                        </th>

                                        <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                            Image
                                        </th>

                                        <th class="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody class="divide-y divide-gray-200">
                                    <tr
                                        v-for="assetLocation in getAssetLocations(experience.uuid)"
                                        :key="assetLocation.uuid"
                                    >
                                        <td class="px-4 py-3 text-sm font-medium text-gray-900">
                                            {{ assetLocation.name }}
                                        </td>

                                        <td class="px-4 py-3">
                                            <code class="text-xs text-gray-600">
                                                {{ assetLocation.uuid }}
                                            </code>
                                        </td>

                                        <td class="px-4 py-3">
                                            <a
                                                v-if="assetLocation.image_url"
                                                :href="assetLocation.image_url"
                                                target="_blank"
                                            >
                                                <img
                                                    :src="assetLocation.image_url"
                                                    :alt="assetLocation.name"
                                                    class="h-12 w-12 rounded-md object-cover"
                                                >
                                            </a>

                                            <span
                                                v-else
                                                class="text-sm text-gray-400"
                                            >
                                                -
                                            </span>
                                        </td>

                                        <td class="px-4 py-3 text-right">
                                            <button
                                                type="button"
                                                class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                                                :disabled="deletingUuid === assetLocation.uuid"
                                                @click="deleteAssetLocation(experience, assetLocation)"
                                            >
                                                {{
                                                    deletingUuid === assetLocation.uuid
                                                        ? 'Deleting...'
                                                        : 'Delete'
                                                }}
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <div
            v-if="pagination.lastPage > 1"
            class="flex items-center justify-between"
        >
            <button
                type="button"
                class="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="pagination.currentPage <= 1"
                @click="loadExperiences(pagination.currentPage - 1)"
            >
                Previous
            </button>

            <span class="text-sm text-gray-500">
                Page {{ pagination.currentPage }}
                of {{ pagination.lastPage }}
            </span>

            <button
                type="button"
                class="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="pagination.currentPage >= pagination.lastPage"
                @click="loadExperiences(pagination.currentPage + 1)"
            >
                Next
            </button>
        </div>
    </div>
</template>

<script>
const emptyAssetLocationForm = () => ({
    uuid: '',
    name: '',
    image: null,
});

export default {
    name: 'ManageAssetLocations',

    data() {
        return {
            experiences: [],
            assetLocations: {},
            forms: {},
            formErrors: {},
            savingExperiences: {},
            loadingAssetLocations: {},
            expandedExperienceUuid: null,
            deletingUuid: null,
            search: '',
            loading: false,
            error: null,
            pagination: {
                currentPage: 1,
                lastPage: 1,
                total: 0,
            },
        };
    },

    mounted() {
        this.loadExperiences();
    },

    methods: {
        async loadExperiences(page = 1) {
            this.loading = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    '/asset-location-experiences',
                    {
                        params: {
                            page,
                            search: this.search || undefined,
                        },
                    }
                );

                this.experiences = response.data.data ?? [];

                this.pagination = {
                    currentPage: response.data.current_page
                        ?? response.data.meta?.current_page
                        ?? 1,
                    lastPage: response.data.last_page
                        ?? response.data.meta?.last_page
                        ?? 1,
                    total: response.data.total
                        ?? response.data.meta?.total
                        ?? 0,
                };

                this.experiences.forEach(experience => {
                    this.ensureExperienceState(experience.uuid);
                });
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load experiences.'
                );
            } finally {
                this.loading = false;
            }
        },

        searchExperiences() {
            this.expandedExperienceUuid = null;
            this.loadExperiences(1);
        },

        clearSearch() {
            this.search = '';
            this.expandedExperienceUuid = null;
            this.loadExperiences(1);
        },

        async toggleExperience(experience) {
            if (this.expandedExperienceUuid === experience.uuid) {
                this.expandedExperienceUuid = null;
                return;
            }

            this.expandedExperienceUuid = experience.uuid;
            this.ensureExperienceState(experience.uuid);

            if (this.assetLocations[experience.uuid] !== null) {
                return;
            }

            await this.loadAssetLocations(experience);
        },

        async loadAssetLocations(experience) {
            this.loadingAssetLocations[experience.uuid] = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `/asset-location-experiences/${experience.uuid}/asset-locations`
                );

                this.assetLocations[experience.uuid] =
                    response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load asset locations.'
                );
            } finally {
                this.loadingAssetLocations[experience.uuid] = false;
            }
        },

        selectImage(experienceUuid, event) {
            const [image] = event.target.files;

            this.forms[experienceUuid].image = image ?? null;
        },

        async createAssetLocation(experience) {
            const form = this.forms[experience.uuid];

            if (!form.uuid || !form.name) {
                this.formErrors[experience.uuid] =
                    'Name and Asset Location UUID are required.';

                return;
            }

            this.savingExperiences[experience.uuid] = true;
            this.formErrors[experience.uuid] = null;

            try {
                const response = await this.$http.post(
                    `/asset-location-experiences/${experience.uuid}/asset-locations`,
                    {
                        uuid: form.uuid.trim(),
                        name: form.name.trim(),
                    }
                );

                let assetLocation = response.data.data ?? response.data;

                if (form.image) {
                    assetLocation = await this.uploadAssetLocationImage(
                        assetLocation.uuid,
                        form.image
                    );
                }

                this.assetLocations[experience.uuid].push(assetLocation);

                this.forms[experience.uuid] =
                    emptyAssetLocationForm();
            } catch (error) {
                if (error.response?.status === 422) {
                    const errors = error.response.data.errors ?? {};

                    this.formErrors[experience.uuid] =
                        Object.values(errors)[0]?.[0]
                        ?? error.response.data.message
                        ?? 'Validation failed.';

                    return;
                }

                this.formErrors[experience.uuid] =
                    this.getErrorMessage(
                        error,
                        'Unable to create asset location.'
                    );
            } finally {
                this.savingExperiences[experience.uuid] = false;
            }
        },

        async uploadAssetLocationImage(assetLocationUuid, image) {
            const formData = new FormData();

            formData.append('image', image);

            const response = await this.$http.post(
                `/asset-locations/${assetLocationUuid}/image`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.data.data ?? response.data;
        },

        async deleteAssetLocation(experience, assetLocation) {
            if (!window.confirm(
                `Delete "${assetLocation.name}"?`
            )) {
                return;
            }

            this.deletingUuid = assetLocation.uuid;
            this.error = null;

            try {
                await this.$http.delete(
                    `/asset-locations/${assetLocation.uuid}`
                );

                this.assetLocations[experience.uuid] =
                    this.assetLocations[experience.uuid].filter(
                        location => location.uuid !== assetLocation.uuid
                    );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to delete asset location.'
                );
            } finally {
                this.deletingUuid = null;
            }
        },

        ensureExperienceState(experienceUuid) {
            if (!(experienceUuid in this.assetLocations)) {
                this.assetLocations[experienceUuid] = null;
            }

            if (!(experienceUuid in this.forms)) {
                this.forms[experienceUuid] =
                    emptyAssetLocationForm();
            }

            if (!(experienceUuid in this.formErrors)) {
                this.formErrors[experienceUuid] = null;
            }

            if (!(experienceUuid in this.savingExperiences)) {
                this.savingExperiences[experienceUuid] = false;
            }

            if (!(experienceUuid in this.loadingAssetLocations)) {
                this.loadingAssetLocations[experienceUuid] = false;
            }
        },

        getAssetLocations(experienceUuid) {
            return this.assetLocations[experienceUuid] ?? [];
        },

        getAssetLocationCount(experienceUuid) {
            return this.assetLocations[experienceUuid]?.length ?? 0;
        },

        getFormError(experienceUuid) {
            return this.formErrors[experienceUuid] ?? null;
        },

        isSaving(experienceUuid) {
            return this.savingExperiences[experienceUuid] ?? false;
        },

        isLoadingAssetLocations(experienceUuid) {
            return this.loadingAssetLocations[experienceUuid] ?? false;
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
    },
};
</script>